/**
 * Renewal date arithmetic, mirroring `backend/src/models/subscriber.js`.
 *
 * The dialog has to show the resulting expiry date before the admin commits to
 * it, so the same rules exist on both sides: whole calendar months with
 * end-of-month clamping, counted from the current expiry while the
 * subscription is still running and from today once it has lapsed. The server
 * remains the authority — it revalidates the month count and recalculates the
 * date from the stored document.
 *
 * Dates are handled as `YYYY-MM-DD` strings in UTC, matching what the API
 * returns and what the backend stores, so the preview cannot drift by a day
 * against the saved result in a non-UTC timezone.
 */

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The month counts the Renew dialog offers; the server validates these too. */
export const RENEWAL_MONTH_OPTIONS = Object.freeze([1, 2, 3, 6, 12]);

function toUtcDate(isoDate) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

/** Today as a UTC date-only string, matching how the backend reads "today". */
export function todayIsoDate(now = new Date()) {
  return toIsoDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));
}

/** Calendar-month addition with end-of-month clamping (Jan 31 + 1 = Feb 28/29). */
export function addCalendarMonths(isoDate, months) {
  const date = toUtcDate(isoDate);
  const targetMonth = date.getUTCMonth() + months;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
  return toIsoDate(new Date(Date.UTC(targetYear, normalizedMonth, Math.min(date.getUTCDate(), lastDay))));
}

/**
 * The date a renewal counts forward from. ISO date strings compare correctly
 * with `>=`, so no parsing is needed to decide whether the term is still live.
 */
export function renewalBaseDate(currentExpiry, now = new Date()) {
  const today = todayIsoDate(now);
  return currentExpiry >= today ? currentExpiry : today;
}

/**
 * @returns {string|null} the expiry the renewal would produce, or null when the
 *   subscriber's stored expiry is missing or malformed and no honest preview
 *   can be shown.
 */
export function previewRenewalExpiry(currentExpiry, months, now = new Date()) {
  if (!DATE_PATTERN.test(currentExpiry || "")) return null;
  if (!RENEWAL_MONTH_OPTIONS.includes(months)) return null;
  return addCalendarMonths(renewalBaseDate(currentExpiry, now), months);
}
